import LexusIcon from '@assets/icons/lexusInfo.svg'
import ToyotaIcon from '@assets/icons/toyotaInfo.svg'
import { getCodeweaversConfiguration } from '@utils/helpers/index-client'
import {
  getContentFile,
  getModels,
  getPageData,
} from '@utils/helpers/index-server'
import {
  customEvent,
  updateDatalayer,
  workflowEvent,
} from '@utils/helpers/tracking'
import axios from 'axios'
import classnames from 'classnames'
import type { GetServerSideProps } from 'next'
import Script from 'next/script'
import { useState } from 'react'
import Modal from 'react-modal'

const QuickFinanceCalculator = ({
  content,
  query,
  dealer,
  model,
  models,
  tracking,
  configVehicle,
  brand,
}) => {
  Modal.setAppElement('#__next')
  const classes = classnames({
    'aff-off': !content.configuration.aff,
    promotions: query.promotions,
    model,
    dealer,
    config: configVehicle,
  })

  const postMessage = ({ modelName, tmeId, extId, data }) => {
    if (window.self !== window.top) {
      const msg = {
        promotionFinanceUpdate: {
          representative: [
            ...document.querySelectorAll('.codeweaversResultsLabel'),
          ].find((e) => e.innerText === 'Representative').nextElementSibling
            .innerText,
          term: data?.Quote?.FinanceDepositAllowance?.Term || null,
          deposit: data?.Quote?.FinanceDepositAllowance?.CashDeposit || null,
          payments:
            data?.Quote?.FinanceDepositAllowance?.RegularPayments || null,
          firstPayment:
            data?.Quote?.FinanceDepositAllowance?.FirstPayment || null,
          fda: data?.Quote?.FinanceDepositAllowance?.Amount || null,
          model: {
            id: tmeId.length !== 0 ? tmeId : null,
            name: modelName.length !== 0 ? modelName : null,
            rangeCode: extId.length !== 0 ? extId : null,
          },
        },
      }
      window.parent.postMessage(JSON.stringify(msg), 'https://www.toyota.co.uk')
    }
  }

  const toggleOptionsDropdown = (e) => {
    e.target.nextElementSibling.classList.toggle('open_dropdown')
  }

  const events = (event, data) => {
    const modelName = data?.Vehicle?.Range || ''
    const bodyTypeName = document.querySelector(
      '#codeweaversDerivatives',
    )?.value
    const gradeName = document.querySelector('#codeweaversVariants')?.value
    const rangeCode = data?.Vehicle?.Identifier || ''
    const extId = data?.Vehicle?.Identifier || ''
    const localModelId = models.find((localModel) => {
      if (!localModel?.finance?.name) {
        return localModel.name.toLowerCase() === modelName.toLowerCase()
      }

      return localModel.finance.name.toLowerCase() === modelName.toLowerCase()
    })?.image?.id
    const tmeId = configVehicle?.car?.modelCode || localModelId || ''
    let eventName = null
    let eventLabel = null
    let triggerWorkflowEvent = true

    document
      .querySelectorAll('#cw_qfq_wrapper #codeweaversFindDealer')
      ?.forEach((select) => {
        select.removeEventListener('click', () => events('dealer', null))
        select.addEventListener('click', () => events('dealer', null))
      })
    document
      .querySelectorAll('#cw_qfq_wrapper.config .codeweaversSelect')
      ?.forEach((select) => {
        select.removeEventListener('click', toggleOptionsDropdown)
        select.addEventListener('click', toggleOptionsDropdown)
      })

    switch (event) {
      case 'apply.start':
        eventName = 'click-cta'
        eventLabel = 'apply'
        triggerWorkflowEvent = false
        break
      case 'print':
        eventName = 'click-cta'
        eventLabel = 'print'
        triggerWorkflowEvent = false
        break
      case 'calculation':
        eventName = 'calculate'
        eventLabel = 'fc-calculate-quote'
        break
      case 'calculation.vehicle_change':
        eventName = 'calculate'
        eventLabel = 'fc-calculate-quote'
        break
      case 'dealer':
        eventName = 'click-cta'
        eventLabel = 'dealer'
        triggerWorkflowEvent = false
        break

      default:
        break
    }

    if (event === 'plugin.loaded') {
      return
    }

    if (event === 'calculation') {
      postMessage({ modelName, tmeId, extId, data })
    }

    if (modelName) {
      updateDatalayer('model-qfc', {
        id: tmeId || '',
        name: modelName,
        productId: rangeCode,
        productName: `${modelName} ${bodyTypeName} ${gradeName}`,
      })
    }

    if (dealer) {
      updateDatalayer('dealer', {
        uuid: dealer.uuid,
        name: dealer.name,
        address: {
          city: dealer.addresses[0].city,
          zip: dealer.addresses[0].zip,
        },
      })
    }

    if (triggerWorkflowEvent) {
      workflowEvent({
        conversion: 1,
        name: tracking.page,
        action: 'workflow-step',
        step: eventLabel,
        variant: tracking.variant,
        page: tracking.page,
      })
    } else {
      customEvent({
        name: 'ctaevent',
        componentName: tracking.page,
        componentGroup: 'tools',
        action: eventName,
        label: eventLabel,
      })
    }
  }

  const initCodeWeavers = () => {
    window.codeweavers.main({
      pluginContentDivId: 'cw_qfq_wrapper',
      customerReference: 'national-site',
      applyUrl: `${window.location.origin}/finance/pre-approval`,
      onEventBroadcast: events,
      ...getCodeweaversConfiguration({
        model,
        configVehicle,
        settings: content,
        query,
      }),
    })
  }

  const TooltipIcon = ({ brand: BrandLogo, ...rest }) => {
    if (BrandLogo === 'lexus') {
      return <LexusIcon {...rest} />
    }

    return <ToyotaIcon {...rest} />
  }

  const RenderContent = ({ data, id }) => {
    const [open, setIsOpen] = useState(false)
    const hasTooltip = !!content?.details?.tooltip
    const hasData = typeof data === 'string'
    const hasTooltipPlacement = hasData && data.includes('[tooltip]')

    if (!hasData) {
      return null
    }

    if (!hasTooltip || !hasTooltipPlacement) {
      return <>{data.replace('[tooltip]', '')}</>
    }

    if (hasTooltipPlacement) {
      return (
        <>
          {data.split('[tooltip]').map((text, index) => {
            return (
              <span key={index}>
                {text}
                {index + 1 !== data.split('[tooltip]').length && (
                  <TooltipIcon
                    brand={brand}
                    className="inline-block ml-1 h-4 cursor-pointer text-base-text"
                    data-tip
                    data-for={`${id}-tooltip`}
                    data-gt-name="componentevent"
                    data-gt-action={`${id}-open-modal`}
                    data-gt-label={`${id}`}
                    onClick={(e) => {
                      e.preventDefault()
                      setIsOpen(true)
                    }}
                  />
                )}
              </span>
            )
          })}
          <Modal
            className={`${
              brand === 'toyota' ? 'rounded' : ''
            } absolute bg-base text-white top-1/2 left-1/2 right-auto bottom-auto mr-[-50%] -translate-y-1/2 -translate-x-1/2 max-w-[90%] md:max-w-screen-sm max-h-[50%] p-8 overflow-y-auto w-full focus:border-opacity/0 outline-none`}
            overlayClassName="fixed top-0 left-0 right-0 bottom-0 z-20 bg-[#FFF]/75"
            id="unsubscribeModal"
            isOpen={open}
            onRequestClose={() => setIsOpen(false)}
          >
            <button
              className="absolute top-3 right-4 text-3xl leading-none"
              onClick={() => setIsOpen(false)}
            >
              &times;
            </button>
            {content?.details?.tooltip}
          </Modal>
        </>
      )
    }

    return <>{data}</>
  }

  return (
    <div
      className={`flex flex-col container ${
        !query?.promotions ? 'min-h-screen' : ''
      } items-center my-8 md:my-10 lg:my-[60px]`}
    >
      {/* eslint-disable-next-line @next/next/no-sync-scripts */}
      <Script
        data-ot-ignore
        id="codeweavers_js"
        src={process.env.NEXT_PUBLIC_CODEWEAVERS}
        onLoad={initCodeWeavers}
      />
      {!query?.promotions && (
        <div className="text-center mb-4">
          <h2 className="text-xl md:text-2xl lg:text-3xl mb-3 !leading-snug">
            <RenderContent data={content?.details?.title} id="title" />
          </h2>
          <p className="text-md !normal-case last:[&>p]:mb-0 [&>p]:mb-3 [&>p>a]:underline max-w-4xl">
            <RenderContent data={content?.details?.subtitle} id="subtitle" />
          </p>
        </div>
      )}
      <div id="cw_qfq_wrapper" className={classes}></div>
    </div>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'quick-finance-calculator'
  const [content, settings] = getContentFile(brand, `tools/${form}`)
  let configVehicle = null

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }
  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  if (context.query?.config || context.query?.configId) {
    const { data } = await axios({
      method: 'get',
      url: `https://kong-proxy-aws.toyota-europe.com/ccsave/config/${
        context.query?.config || context.query?.configId
      }/json/full`,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    configVehicle = data
  }

  return {
    props: {
      models: getModels(brand),
      configVehicle,
      content: content.attributes,
      title: content.attributes.title,
      tracking: {
        page: content.attributes.tracking?.page,
        workflow: 'quick-finance-calculator',
        subCategory: 'finance',
        variant: context.query?.promotions ? 'promotions' : '',
        initialStep: 'plugin.loaded',
        enableWorkflow: false,
      },
      ...(await getPageData(context)),
    },
  }
}

export default QuickFinanceCalculator
